import { useRef, useEffect, useCallback } from "react";

interface GridBackgroundProps {
  color?: string;
  dotSize?: number;
  gap?: number;
  speed?: number;
  opacity?: number;
}

export default function GridBackground({
  color = "97, 206, 112",
  dotSize = 1.2,
  gap = 28,
  speed = 0.003,
  opacity = 0.25,
}: GridBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const timeRef = useRef(0);
  const animRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
    }

    ctx.clearRect(0, 0, w, h);
    timeRef.current += speed;
    const t = timeRef.current;
    const mx = mouseRef.current.x;
    const my = mouseRef.current.y;
    const proximity = 120;

    const cols = Math.ceil(w / gap) + 1;
    const rows = Math.ceil(h / gap) + 1;
    const offsetX = (w - (cols - 1) * gap) / 2;
    const offsetY = (h - (rows - 1) * gap) / 2;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = offsetX + c * gap;
        const y = offsetY + r * gap;

        // Subtle wave animation
        const wave = Math.sin(t + c * 0.3 + r * 0.2) * 0.5 + 0.5;
        const pulse = 0.3 + wave * 0.7;

        // Mouse proximity glow
        const dx = x - mx;
        const dy = y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const hover = dist < proximity ? 1 - dist / proximity : 0;

        const dotOpacity = (pulse * 0.4 + hover * 0.6) * opacity;
        const size = dotSize + hover * 1.5;

        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${dotOpacity})`;
        ctx.fill();

        // Cross lines connecting nearby dots on hover
        if (hover > 0.3) {
          ctx.strokeStyle = `rgba(${color}, ${hover * 0.12 * opacity})`;
          ctx.lineWidth = 0.5;
          // horizontal
          if (c < cols - 1) {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + gap, y);
            ctx.stroke();
          }
          // vertical
          if (r < rows - 1) {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x, y + gap);
            ctx.stroke();
          }
        }
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, [color, dotSize, gap, speed, opacity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handleLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseleave", handleLeave);
    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseleave", handleLeave);
    };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
